import { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslation } from "i18n-fe";
import { useRingData } from "../context/RingDataContext";
import { ROW_KIND } from "../utils/orderOfGo";
import ActiveAlert from "./ActiveAlert";

// #3391 / #3596 -- active alerts raised at a scribe station, shown here so the
// announcer sees them without being there. The data is the EXISTING utility-event
// record on the ride (Ride.utilityEvents, card #139/#1991), read off the same
// GET /ride the running order already polls on the shared entity tick: no new
// hook, no new poll.
//
// The alert on the ride IN THE RING shows inside the In the Ring Now panel
// (LiveDesk), not here. This strip is only the one count chip for active alerts
// on OTHER rides; it opens a list with Dismiss on each. Nothing renders when
// there are none, so the band row keeps its full height.
//
// Alerts are built from `allRows` (every ride of the ring), NOT the day-scoped
// `rows`: an open welfare alert on a ride outside the today-only filter (showAllDays
// defaults off) must still reach the announcer.
export default function UtilityAlertsBar() {
  const { t } = useTranslation("announcer");
  const { allRows, currentRideId } = useRingData();
  const [open, setOpen] = useState(false);

  const alerts = useMemo(() => {
    const out = [];
    for (const row of allRows || []) {
      if (row.kind !== ROW_KIND.RIDE) continue;
      if (currentRideId && row.id === currentRideId) continue;
      for (const event of row.utilityEvents || []) out.push({ row, event });
    }
    return out.sort((a, b) => String(a.event.raisedAt).localeCompare(String(b.event.raisedAt)));
  }, [allRows, currentRideId]);

  // The list closes itself once the last alert is gone, so a later alert does
  // not pop it open again.
  const none = alerts.length === 0;
  useEffect(() => {
    if (none) setOpen(false);
  }, [none]);

  if (none) return null;

  return (
    <Box
      data-testid="utility-alerts-bar"
      role="region"
      aria-label={t("alerts.heading")}
      sx={{ px: 3, py: 0.75, borderBottom: 1, borderColor: "divider" }}
    >
      <Chip
        data-testid="utility-alerts-chip"
        icon={<WarningAmberIcon />}
        label={t("alerts.otherRidesChip", { count: alerts.length })}
        onClick={() => setOpen(true)}
        sx={{
          fontWeight: 700,
          backgroundColor: "#b45309",
          color: "#fff",
          "& .MuiChip-icon": { color: "#fff" },
          "&:hover": { backgroundColor: "#92400e" },
        }}
      />
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("alerts.listTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={1} data-testid="utility-alerts-list" sx={{ pt: 1 }}>
            {alerts.map(({ row, event }) => (
              <ActiveAlert key={event.id} row={row} event={event} showRide />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("alerts.listClose")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
